import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Modal, Table } from 'react-bootstrap';
import { getDatabase, ref, onValue, push, update, remove } from 'firebase/database';
import { FaEdit, FaTrash } from 'react-icons/fa';

const Teachers = () => {
  const [teachers, setTeachers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    qualification: '',
    experience: '',
    joiningDate: '',
    status: 'Active'
  });

  useEffect(() => {
    const db = getDatabase();
    const teachersRef = ref(db, 'teachers');
    const unsubscribe = onValue(teachersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const teachersList = Object.entries(data).map(([id, teacher]) => ({
          id,
          ...teacher
        }));
        setTeachers(teachersList);
      } else {
        setTeachers([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const db = getDatabase();
    
    if (editingTeacher) {
      await update(ref(db, `teachers/${editingTeacher.id}`), formData);
    } else {
      await push(ref(db, 'teachers'), formData);
    }
    
    handleClose();
  };

  const handleEdit = (teacher) => {
    setEditingTeacher(teacher);
    setFormData(teacher);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this teacher?')) {
      const db = getDatabase();
      await remove(ref(db, `teachers/${id}`));
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingTeacher(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      qualification: '',
      experience: '',
      joiningDate: '',
      status: 'Active'
    });
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontWeight: 700, color: '#1976d2' }}>Teachers Management</h2>
        <Button 
          variant="primary" 
          onClick={() => setShowModal(true)}
          style={{ borderRadius: '8px', padding: '8px 16px' }}
        >
          Add New Teacher
        </Button>
      </div>

      <Row className="g-4">
        {teachers.map((teacher) => (
          <Col key={teacher.id} lg={4} md={6}>
            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
              <Card.Body>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <h5 style={{ fontWeight: 600, color: '#1976d2' }}>{teacher.name}</h5>
                  <div>
                    <Button 
                      variant="link" 
                      onClick={() => handleEdit(teacher)}
                      style={{ padding: '4px' }}
                    >
                      <FaEdit color="#1976d2" />
                    </Button>
                    <Button 
                      variant="link" 
                      onClick={() => handleDelete(teacher.id)}
                      style={{ padding: '4px' }}
                    >
                      <FaTrash color="#d32f2f" />
                    </Button>
                  </div>
                </div>
                <Table size="sm" borderless>
                  <tbody>
                    <tr>
                      <td><strong>Subject:</strong></td>
                      <td>{teacher.subject}</td>
                    </tr>
                    <tr>
                      <td><strong>Email:</strong></td>
                      <td>{teacher.email}</td>
                    </tr>
                    <tr>
                      <td><strong>Phone:</strong></td>
                      <td>{teacher.phone}</td>
                    </tr>
                    <tr>
                      <td><strong>Experience:</strong></td>
                      <td>{teacher.experience} years</td>
                    </tr>
                    <tr>
                      <td><strong>Status:</strong></td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: teacher.status === 'Active' ? '#4caf50' : '#f44336',
                          color: 'white',
                          fontSize: '12px'
                        }}>
                          {teacher.status}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Subject</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Qualification</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.qualification}
                    onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Experience (years)</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Joining Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.joiningDate}
                    onChange={(e) => setFormData({ ...formData, joiningDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingTeacher ? 'Update' : 'Add'} Teacher
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Teachers; 